import {
  QuestionAttempt,
  QuestionAttemptResponse,
  Questionnaire,
  QuestionnaireStep,
} from 'APIs/letrusAPI/questionnaire/types';
import {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {postPracticeTestAnswerRequest} from 'store/reducers/questionnaires';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import useIndexedDB from 'utils/hooks/useIndexedDB';
import {usePrevious} from 'utils/hooks/usePrevious';
import {
  EnrichedQuestion,
  SubmitQuestionAnswerParams,
  QuestionAnswer,
} from 'utils/types/questionnaires';

interface ResponsesData {
  selectedChoices?: number[];
  correctChoices?: number[];
  wrongChoices?: number[];
  correctChoice?: number;
  selectedChoice?: number;
  isCorrect?: boolean;
  hasFeedback?: boolean;
}

export interface UsePracticeTestProps {
  questionnaire?: Questionnaire;
  questionAttempt?: QuestionAttempt[];
  learningPathUserInstanceId?: string | number;
  learningPathStepId?: string | number;
  onNextQuestionCallback?(questionAttemptData: QuestionAttempt): void;
}

function usePracticeTest({
  questionnaire,
  questionAttempt,
  learningPathUserInstanceId,
  learningPathStepId,
  onNextQuestionCallback = () => {},
}: UsePracticeTestProps) {
  const dispatch = useDispatch();
  const [currentQuestion, setCurrentQuestion] = useState<EnrichedQuestion>(
    {} as EnrichedQuestion,
  );
  const [currentOrder, setCurrentOrder] = useState(1);
  const [isLocalAnswerUpdated, setIsLocalAnswerUpdated] = useState(false);

  const questionnaireIDB = useIndexedDB<Partial<QuestionAttempt>>(
    'LocalQuestionnaires',
  );
  const {user} = getUserData();

  const previousQuestionnaireAnswersLength = usePrevious(
    questionAttempt?.length,
    0,
  );

  useEffect(() => {
    const currentStep = questionnaire?.steps[currentOrder - 1];

    if (currentStep) {
      enrichQuestionData(currentStep).then(setCurrentQuestion);
    }
  }, [questionnaire, questionAttempt, currentOrder, isLocalAnswerUpdated]);

  // Awaiting the question answer state to be filled before calling the respective callback
  useEffect(() => {
    const currentQuestionnaireAnswersLength = questionAttempt?.length || 0;

    if (
      currentQuestionnaireAnswersLength > previousQuestionnaireAnswersLength!
    ) {
      const currentQuestionAnswer = questionAttempt?.slice(-1)[0];
      if (currentQuestionAnswer) {
        onNextQuestionCallback(currentQuestionAnswer);
      }
    }
  }, [questionAttempt, currentQuestion]);

  /**
   * Generates the key for the IndexedDB using the current learning path user
   * instance ID, step ID, user ID, and question ID.
   * @param questionId The ID of the current question.
   * @returns The key for the IndexedDB.
   */
  function generateIDBKeyForAnswer(questionId: number | undefined): string {
    return `lpui${learningPathUserInstanceId || ''}-step${
      learningPathStepId || ''
    }-user${user.id}-question${questionId}`;
  }

  /**
   * Fetches the question data from the database and enriches it with the current
   * user's answer. If the user has answered the question before, it will use the
   * offline answer. If not, it will use the online answer.
   * @param step The current step object.
   * @returns The enriched question data.
   */
  async function enrichQuestionData(
    step: QuestionnaireStep,
  ): Promise<EnrichedQuestion> {
    // Find the online answer for the current step
    const onlineAnswer = questionAttempt?.find(
      (answer) => answer.question === step?.question.id,
    );

    const answerKey = generateIDBKeyForAnswer(step?.question.id);

    // Fetch the offline answer from IndexedDB
    const offlineAnswer = await questionnaireIDB.getItem(answerKey);

    // Get the question answer from the online or offline answer
    const questionAnswer = onlineAnswer ?? offlineAnswer;

    // Determine responses based on question type
    let responses: ResponsesData;
    if (step?.question.question_type === 'checkbox') {
      responses = getCheckboxAnswerData(
        questionAnswer?.responses,
        questionAnswer?.is_correct,
      );
    } else {
      responses = getRadioAnswerData(questionAnswer?.responses);
    }

    // Enrich the question data with the user's answer
    return {
      ...step?.question,
      stepId: step?.id,
      order: step?.order,
      answerId: questionAnswer?.id,
      responses,
      answer: questionAnswer?.answer ?? '',
      open_question_feedback: questionAnswer?.open_question_feedback,
    };
  }

  function getRadioAnswerData(
    userAnswer?: QuestionAttemptResponse[],
  ): ResponsesData {
    const selectedChoice = userAnswer?.[0];
    const correctChoice = userAnswer?.find(
      (choice) => choice.is_response_correct,
    )?.option;

    return {
      selectedChoice: selectedChoice?.option,
      correctChoice,
      isCorrect: selectedChoice?.is_response_correct,
      hasFeedback: hasFeedback(userAnswer),
    };
  }

  function getCheckboxAnswerData(
    userAnswer?: QuestionAttemptResponse[],
    isCorrectFromBackend?: boolean,
  ): ResponsesData {
    const selectedChoices =
      userAnswer
        ?.map((choice) => choice.option)
        ?.filter((opt): opt is number => opt !== undefined) ?? [];
    const correctChoices =
      userAnswer
        ?.filter((choice) => choice.is_response_correct)
        ?.map((choice) => choice.option)
        ?.filter((opt): opt is number => opt !== undefined) ?? [];

    const isCorrect =
      userAnswer && userAnswer.length > 0
        ? userAnswer.every((choice) => choice.is_response_correct)
        : isCorrectFromBackend;

    return {
      selectedChoices,
      correctChoices,
      isCorrect,
      hasFeedback: hasFeedback(userAnswer),
    };
  }

  function hasFeedback(userAnswer?: QuestionAttemptResponse[]) {
    const hasAtLeastOneChoiceFeedback = userAnswer?.some(
      (choice) => choice.feedback,
    );

    const hasGeneralFeedback =
      !!currentQuestion?.correct_feedback ||
      !!currentQuestion?.incorrect_feedback ||
      !!currentQuestion?.has_correct_answer;

    return hasGeneralFeedback || hasAtLeastOneChoiceFeedback;
  }

  /**
   * Extracts option IDs from response array, filtering undefined values.
   * Used for comparing checkbox and radio responses.
   *
   * @param responses - Array of responses to extract options from
   * @returns Sorted array of valid option IDs
   *
   */
  function extractOptionIds(
    responses?: (QuestionAttemptResponse | Partial<QuestionAttemptResponse>)[],
  ): number[] {
    return (responses ?? [])
      .map((response) => response.option)
      .filter((option): option is number => option !== undefined)
      .sort((a, b) => a - b);
  }

  /**
   * Compares two response arrays to check if they differ.
   * Order-independent comparison - works for both radio (single) and checkbox (multiple).
   *
   * @param oldResponses - Previous responses from backend
   * @param newResponses - New responses to submit
   * @returns true if responses are different, false if identical
   */
  function areResponsesDifferent(
    oldResponses?: QuestionAttemptResponse[],
    newResponses?: Partial<QuestionAttemptResponse>[],
  ): boolean {
    const oldOptions = extractOptionIds(oldResponses);
    const newOptions = extractOptionIds(newResponses);

    // Check length first (quick exit)
    if (oldOptions.length !== newOptions.length) return true;

    // Compare sorted arrays element by element
    return oldOptions.some((option, index) => option !== newOptions[index]);
  }

  /**
   * Compares if a question answer has changed (handles both answer and responses fields).
   * Works for all question types: radio, checkbox, text, rating.
   *
   * @param oldAnswer - Previous answer from backend/indexedDB
   * @param newAnswer - New answer to submit
   * @returns true if answer or responses changed, false if identical
   *
   */
  function isQuestionAnswerDifferent(
    oldAnswer?: Partial<QuestionAttempt> | null,
    newAnswer?: Partial<QuestionAnswer>,
  ): boolean {
    if (!oldAnswer && !newAnswer) return false;

    // Check if responses changed (radio, checkbox)
    const newResponses = newAnswer?.responses;
    const responsesChanged = areResponsesDifferent(
      oldAnswer?.responses,
      newResponses,
    );

    // Check if answer changed (text, rating)
    const answerChanged = oldAnswer?.answer !== newAnswer?.answer;

    return responsesChanged || answerChanged;
  }

  async function submitQuestionAnswer({newAnswer}: SubmitQuestionAnswerParams) {
    const answerKey = generateIDBKeyForAnswer(newAnswer.question);
    const offlineAnswer = await questionnaireIDB.getItem(answerKey);
    const onlineAnswer = questionAttempt?.find(
      (oldAnswer) => oldAnswer.question === newAnswer.question,
    );
    const isOnlineAnswerDifferent = isQuestionAnswerDifferent(
      onlineAnswer,
      newAnswer,
    );
    const isOfflineAnswerDifferent = isQuestionAnswerDifferent(
      offlineAnswer,
      newAnswer,
    );
    /**
     * Determines if this is the first answer.
     * This is true when both online and offline answers are undefined.
     */
    const isFirstAnswer =
      onlineAnswer === undefined && offlineAnswer === undefined;

    setIsLocalAnswerUpdated(false);

    if (isFirstAnswer || isOnlineAnswerDifferent || isOfflineAnswerDifferent) {
      dispatch(postPracticeTestAnswerRequest(newAnswer));

      questionnaireIDB
        .setItem(answerKey, {
          ...newAnswer,
          responses: newAnswer.responses,
        })
        .then(() => {
          setIsLocalAnswerUpdated(true);
        });
    }
  }

  function returnToPreviousQuestion(errorCallback?: () => void) {
    const previousQuestion = questionnaire?.steps.find(
      (question) => question.order === currentQuestion.order - 1,
    );

    if (previousQuestion) {
      setCurrentOrder(previousQuestion.order);
    } else if (typeof errorCallback === 'function') {
      errorCallback();
    }
  }

  function goToNextQuestion(errorCallback?: () => void) {
    const nextQuestion = questionnaire?.steps.find(
      (question) => question.order === currentQuestion?.order + 1,
    );

    if (nextQuestion) {
      setCurrentOrder(nextQuestion.order);
    } else if (typeof errorCallback === 'function') {
      errorCallback();
    }
  }

  function goToQuestionByOrder(order: number) {
    const step = questionnaire?.steps.find(
      (question) => question.order === order,
    );
    if (step) {
      setCurrentOrder(step.order);
    }
  }

  return {
    questionnaire,
    currentQuestion,
    submitQuestionAnswer,
    returnToPreviousQuestion,
    goToNextQuestion,
    goToQuestionByOrder,
  };
}

export default usePracticeTest;
